import { useQuery } from "@tanstack/react-query";
import { addMinutes } from "date-fns";
import { usePostHog } from "posthog-js/react";
import { useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";

import type { AddressDto, CompanyDto, LanguageDto, UserCreateRequest, UserDto, UserUpdateRequest } from "@/api/types";
import { AutoTranslationLanguageSelector } from "@/components/AutoTranslation/AutoTranslationLanguageSelector";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormDatePicker } from "@/components/Form/FormDatePicker";
import { FormImageInput } from "@/components/Form/FormImageInput";
import { FormInput } from "@/components/Form/FormInput";
import { FormRadioCard, FormRadioCardGroup } from "@/components/Form/FormRadioCard";
import { FormSearchableSelect } from "@/components/Form/FormSearchableSelect";
import { FormSelect } from "@/components/Form/FormSelect";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { formatAddress } from "@/helpers/address";
import { isValidEmail, useSuggestedEmailFix } from "@/helpers/email-validation";
import { toUiLanguageId } from "@/helpers/languages";
import { isHttpError } from "@/helpers/Network/errors";
import {
  createEmailValidRule,
  createImageSizeRule,
  createImageTypeRule,
  createRequiredStringRule,
} from "@/helpers/rules";
import { isValidPhoneNumber } from "@/helpers/validation";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useBool } from "@/hooks/useBool";
import { usePermission } from "@/hooks/usePermission";
import { useScrollToHash } from "@/hooks/useScrollToHash";
import { useSlug } from "@/hooks/useSlug";
import { useAddressQueries } from "@/queries/addresses/queryOptions";
import { useCompanyQueries } from "@/queries/companies/queryOptions";
import { useGeneralQueries } from "@/queries/general/queryOptions";
import { usePermissionQueries } from "@/queries/permissions/queryOptions";
import { userMutations } from "@/queries/users/mutations";
import { routes } from "@/routes";
import type { Languages } from "@/translations";
import { Trans, useTranslation } from "@/translations";

import { canAssignRoles } from "../../permissions";

const SUPER_ADMIN_ROLE = { id: "superAdmin", name: "Super Admin", isAdmin: true };
const MAX_LENGTH = 255;

interface CreateOrEditUserFormValues {
  firstName: string;
  lastName: string;
  mobileNumber?: string;
  email: string;
  address?: AddressDto | null;
  company?: CompanyDto | null;
  occupation?: string;
  uiLanguage?: LanguageDto;
  role?: { id: string; name: string; isAdmin: boolean };
  avatar: FormImage[];
  chatPermission?: UserDto["chatPermission"];
  deactivateAfter: boolean;
  deactivateAfterDate: Date | undefined;
  isOnlyAllowedOwnTickets: boolean;
  isAssignableToTickets: boolean;
  onboardingComplete: boolean;
  addMore: boolean;
  isAutoTranslationEnabled?: boolean;
  autoTranslationLanguageId?: string;
}

export function CreateUserPage(): React.ReactNode {
  const [isCancelModalOpened, cancelModalHandler] = useBool(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const slug = useSlug();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sessionUser = useSessionUser();
  const hasPermission = usePermission();
  const postHog = usePostHog();
  const { uploadFormImage, isUploadingImage } = useUploadImage();

  const addressQueries = useAddressQueries();
  const companyQueries = useCompanyQueries();
  const permissionQueries = usePermissionQueries();
  const generalQueries = useGeneralQueries();
  const checkEmailExistsMutation = userMutations.useCheckEmailExists();
  const addUserMutation = userMutations.useAddUser();
  const {
    data: addressesData,
    isLoading: isLoadingAddressesData,
    error: errorAddressesData,
  } = useQuery({
    ...addressQueries.list(),
    enabled: sessionUser.project.type === "addressBased",
  });
  const {
    data: companiesData,
    isLoading: isLoadingCompaniesData,
    error: errorCompaniesData,
  } = useQuery({
    ...companyQueries.list(),
    enabled: sessionUser.project.type === "companyBased",
  });
  const {
    data: rolesData = [],
    isLoading: isLoadingRoles,
    error: errorLoadingRoles,
  } = useQuery(permissionQueries.listRoles());
  const {
    data: uiLanguages = [],
    isPending: isPendingUiLanguages,
    error: errorUiLanguages,
  } = useQuery(generalQueries.languages({ subset: "ui" }));
  const {
    data: autoTranslationLanguages = [],
    isPending: isPendingAutoTranslationLanguages,
    error: errorAutoTranslationLanguages,
  } = useQuery(generalQueries.languages({ subset: "autoTranslation" }));

  const roles = rolesData.map((role) => ({
    id: role.id,
    name: role.name,
    isAdmin: !role.isResidentType,
  }));
  if (sessionUser.isSuperAdmin) {
    roles.push(SUPER_ADMIN_ROLE);
  }
  const defaultRole = roles.find((x) => !x.isAdmin);
  const addresses = addressesData?.items ?? [];
  const companies = companiesData?.items ?? [];
  useScrollToHash(false);

  const form = useForm<CreateOrEditUserFormValues>({
    values: {
      firstName: "",
      lastName: "",
      mobileNumber: "",
      email: "",
      address: undefined,
      company: undefined,
      occupation: "",
      role: defaultRole,
      avatar: [],
      chatPermission: "enabled",
      deactivateAfter: false,
      deactivateAfterDate: undefined,
      addMore: false,
      onboardingComplete: false,
      isOnlyAllowedOwnTickets: false,
      isAssignableToTickets: false,
      uiLanguage: uiLanguages?.[0],
      autoTranslationLanguageId: undefined,
      isAutoTranslationEnabled: false,
    },
    mode: "onSubmit",
  });

  const deactivateAfter = useWatch({ control: form.control, name: "deactivateAfter" });
  const currRole = useWatch({ control: form.control, name: "role" });
  const currEmail = useWatch({ control: form.control, name: "email" });
  const currAutoTranslationLanguageId = useWatch({
    name: "autoTranslationLanguageId",
    control: form.control,
  });
  const currAutoTranslationLanguage =
    autoTranslationLanguages.find((lang) => lang.id === currAutoTranslationLanguageId) ?? null;

  const [minActivationDate] = useState(() => addMinutes(new Date(), 15));
  const currSuggestedEmail = useSuggestedEmailFix(currEmail);

  const triggerEmailValidation = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const isTaken = await isEmailAlreadyTaken(event.target.value);
    if (isTaken === undefined) {
      return;
    }

    if (isTaken) {
      form.setError("email", { message: t("page.create-user.form.email.error.already-exists") });
    } else {
      form.clearErrors("email");
    }
  };

  const isEmailAlreadyTaken = async (email: string) => {
    email = email.trim();

    if (!canEmailBeEdited) {
      return;
    }

    if (!hasPermission((x) => x.userManagement.canListUsers)) {
      return;
    }

    if (!email || !isValidEmail(email)) {
      return;
    }

    const emailExists = await checkEmailExistsMutation.mutateAsync(email).catch(() => undefined);

    if (!emailExists) {
      return;
    }

    if (emailExists.exists) {
      return true;
    }

    return false;
  };

  const goBack = () => {
    form.reset();
    cancelModalHandler.setFalse();
    void navigate(-1);
  };

  const handleSubmit = async (formValues: CreateOrEditUserFormValues) => {
    try {
      await submit(formValues, false);
    } catch {
      // Upload and creation mutations already display failure feedback.
    }
  };

  const submit = async (formValues: CreateOrEditUserFormValues, _applyToAllProjects: boolean) => {
    if (!formValues.uiLanguage) {
      return;
    }

    if (await isEmailAlreadyTaken(formValues.email)) {
      form.setError("email", { message: t("page.create-user.form.email.error.already-exists") }, { shouldFocus: true });

      return;
    }

    const payload: UserCreateRequest | UserUpdateRequest = {
      email: formValues.email.trim(),
      firstName: formValues.firstName.trim(),
      lastName: formValues.lastName.trim(),
      uiLanguage: toUiLanguageId(formValues.uiLanguage.id),
      autoTranslationLanguage: currAutoTranslationLanguage?.id,
      mobileNumber: formValues.mobileNumber?.trim() ?? "",
      deactivateAfter:
        formValues.deactivateAfter && formValues.deactivateAfterDate
          ? formValues.deactivateAfterDate.toISOString()
          : undefined,
      chatPermission: formValues.chatPermission,
      isOnlyAllowedOwnTickets: formValues.isOnlyAllowedOwnTickets,
      isAssignableToAllTickets: formValues.isAssignableToTickets,
      occupation: formValues.occupation?.trim(),
      onboardingCompleted: formValues.onboardingComplete,
      companyId: formValues.company?.id,
      addressId: formValues.address?.id,
    };

    if (formValues.avatar[0]) {
      const uploadedImage = await uploadFormImage(formValues.avatar[0]);

      payload.avatarId = uploadedImage?.id;
    }

    if (hasPermission(canAssignRoles)) {
      if (formValues.role?.id === "superAdmin") {
        payload.isSuperAdmin = true;
      } else {
        payload.roleId = formValues.role?.id;
      }
    }

    let createdUserId: string;
    try {
      createdUserId = await addUserMutation.mutateAsync(payload as UserCreateRequest);
    } catch (error) {
      if (isHttpError(error) && error.status === 400 && (await isEmailAlreadyTaken(formValues.email))) {
        form.setError(
          "email",
          { message: t("page.create-user.form.email.error.already-exists") },
          { shouldFocus: true },
        );
      }

      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    if (formValues.addMore) {
      form.reset();
      form.setValue(
        "address",
        addresses.find((address) => address.id === payload.addressId),
      );
      form.setValue(
        "company",
        companies.find((company) => company.id === payload.companyId),
      );
      form.setValue(
        "uiLanguage",
        uiLanguages.find((language) => language.id === payload.uiLanguage),
      );
      form.setValue("addMore", true);
    } else if (sessionUser.isSuperAdmin && formValues.role?.isAdmin) {
      void navigate(routes.adminNotifications.otherUserSettings({ slug, userId: createdUserId }), {
        replace: true,
      });
    } else {
      void navigate(routes.users.list({ slug }), { replace: true });
    }
  };

  const sortedUiLanguages = useMemo(() => {
    const priorityLanguageIds: Languages[] = ["en", "nl", "de"];

    return uiLanguages
      .toSorted((a, b) => a.description.localeCompare(b.description))
      .toSorted((a, b) => {
        const aIndex = priorityLanguageIds.indexOf(a.id);
        const bIndex = priorityLanguageIds.indexOf(b.id);

        return aIndex - bIndex;
      });
  }, [uiLanguages]);

  const error =
    errorAddressesData || errorCompaniesData || errorLoadingRoles || errorUiLanguages || errorAutoTranslationLanguages;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const loading =
    isLoadingAddressesData ||
    isLoadingCompaniesData ||
    isLoadingRoles ||
    isPendingUiLanguages ||
    isPendingAutoTranslationLanguages;
  if (loading) {
    return <FullSizeLoader withPadding />;
  }

  const title = t("page.create-user.title");
  const canEmailBeEdited = true;
  const canEditUserAddress = true;
  const isAddressRequired = currRole && !currRole.isAdmin;
  const isSubmitting = addUserMutation.isPending || isUploadingImage;

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        contentSize="constrained"
        tabTitle={title}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: t("page.user-overview.title"),
                to: routes.users.list({
                  slug,
                }),
              },
              {
                name: title,
              },
            ]}
          />
        }
      >
        <Form.Root formMethods={form} onSubmit={handleSubmit}>
          <Form.Container>
            <Form.Body>
              {sessionUser.isAdmin && currRole && (
                <FormField label={t("page.create-user.form.role.label")} required>
                  <FormSelect<CreateOrEditUserFormValues, (typeof roles)[number]>
                    name="role"
                    placeholder={t("page.create-user.form.role.placeholder")}
                    items={roles}
                    keySelector={(x) => x.id}
                    renderOption={(x) => x.name}
                    disabled={!hasPermission(canAssignRoles)}
                    rules={{
                      required: t("components.form.error.required", {
                        inputName: t("page.create-user.form.role.label"),
                      }),
                    }}
                    onChange={(value) => {
                      if (!value) {
                        return;
                      }

                      // Trigger address/company validation when changing role AND at least one other field is dirty
                      if (Object.keys(form.formState.dirtyFields).some((key) => key !== "role")) {
                        void form.trigger(["address", "company"]);
                      }

                      // Reset defaults when switching between admin/non-admin
                      if (sessionUser.isSuperAdmin) {
                        form.setValue("onboardingComplete", value.isAdmin);
                        form.setValue("chatPermission", value.isAdmin ? "sendOnly" : "enabled");
                      }

                      // Residents are never assignable to tickets
                      if (!value.isAdmin) {
                        form.setValue("isAssignableToTickets", false);
                        form.setValue("isOnlyAllowedOwnTickets", false);
                      }
                    }}
                  />
                </FormField>
              )}
              {sessionUser.isAdmin && (
                <FormField label={t("page.create-user.form.avatar.label")}>
                  <FormImageInput<CreateOrEditUserFormValues, "avatar">
                    ref={fileInputRef}
                    name="avatar"
                    rules={{
                      validate: {
                        size: createImageSizeRule({ t }),
                        type: createImageTypeRule({ t }),
                      },
                    }}
                    isAvatarInput
                    cropper
                  />
                </FormField>
              )}
              <FormField label={t("page.create-user.form.first-name.label")} required>
                <FormInput<CreateOrEditUserFormValues>
                  name="firstName"
                  placeholder={t("page.create-user.form.first-name.placeholder")}
                  rules={{
                    validate: {
                      required: createRequiredStringRule(t, "page.create-user.form.first-name.label"),
                    },
                    maxLength: {
                      message: t("components.form.error.max-length", { length: MAX_LENGTH }),
                      value: MAX_LENGTH,
                    },
                  }}
                />
              </FormField>
              <FormField label={t("page.create-user.form.last-name.label")} required>
                <FormInput<CreateOrEditUserFormValues>
                  name="lastName"
                  placeholder={t("page.create-user.form.last-name.placeholder")}
                  rules={{
                    validate: {
                      required: createRequiredStringRule(t, "page.create-user.form.last-name.label"),
                    },
                    maxLength: {
                      message: t("components.form.error.max-length", { length: MAX_LENGTH }),
                      value: MAX_LENGTH,
                    },
                  }}
                />
              </FormField>
              <FormField label={t("page.create-user.form.mobile.label")}>
                <FormInput<CreateOrEditUserFormValues, "mobileNumber">
                  name="mobileNumber"
                  placeholder={t("page.create-user.form.mobile.placeholder")}
                  type="tel"
                  inputMode="tel"
                  rules={{
                    validate: {
                      isValid(value) {
                        value = value?.trim();

                        if (!value) {
                          return;
                        }

                        return isValidPhoneNumber(value) ? undefined : t("components.form.error.invalid-phone-number");
                      },
                    },
                  }}
                />
              </FormField>
              <FormField label={t("page.create-user.form.email.label")} required>
                <Inline gap="sm" align="center" wrap={false}>
                  <FormInput<CreateOrEditUserFormValues, "email">
                    name="email"
                    placeholder={t("page.create-user.form.email.placeholder")}
                    type="email"
                    inputMode="email"
                    disabled={!canEmailBeEdited}
                    rules={{
                      onBlur: (event) => triggerEmailValidation(event),
                      validate: {
                        required: createRequiredStringRule(t, "page.create-user.form.email.label"),
                        isValid: createEmailValidRule(t),
                      },
                    }}
                  />
                  {currSuggestedEmail && (
                    <p className="mt-space-xs flex flex-row items-baseline text-text-secondary">
                      <Trans
                        i18nKey="page.create-user.form.email.suggestion"
                        values={{ email: currSuggestedEmail }}
                        components={{
                          suggestion: (value) => (
                            <span className="ml-space-xs">
                              <Button
                                variant="ghostPrimary"
                                onClick={() => {
                                  form.setValue("email", currSuggestedEmail);
                                  form.clearErrors("email");
                                  postHog.capture("email_suggestion_clicked", {
                                    email: currSuggestedEmail,
                                  });
                                }}
                              >
                                {value}
                              </Button>
                            </span>
                          ),
                        }}
                      />
                    </p>
                  )}
                </Inline>
              </FormField>
              {/* Regular address field */}
              {sessionUser.project.type === "addressBased" && (
                <FormField label={t("page.create-user.form.address.label")} required={isAddressRequired}>
                  <FormSearchableSelect<CreateOrEditUserFormValues, AddressDto>
                    name="address"
                    placeholder={t("page.create-user.form.address.placeholder")}
                    items={addresses}
                    keySelector={(x) => x.id}
                    renderOption={(x) => formatAddress(x)}
                    searchableFieldSelector={(x) => [formatAddress(x)]}
                    disabled={!canEditUserAddress}
                    {...(!isAddressRequired && canEditUserAddress
                      ? { isClearable: true, clearLabel: t("common.action.clear") }
                      : { isClearable: false })}
                    rules={{
                      validate: {
                        required: (value) => {
                          // This needs to be inside the validation function to get the latest value
                          const selectedRole = form.getValues("role");

                          if (value || selectedRole?.isAdmin) {
                            return undefined;
                          }

                          return t("components.form.error.required", {
                            inputName: t("page.create-user.form.address.label"),
                          });
                        },
                      },
                    }}
                  />
                </FormField>
              )}
              {/* Company address field */}
              {sessionUser.project.type === "companyBased" && (
                <FormField label={t("page.create-user.form.company.label")} required={isAddressRequired}>
                  <FormSearchableSelect<CreateOrEditUserFormValues, CompanyDto>
                    name="company"
                    placeholder={t("page.create-user.form.company.placeholder")}
                    items={companies}
                    keySelector={(x) => x.id}
                    renderOption={(x) => x.name}
                    searchableFieldSelector={(x) => [x.name]}
                    disabled={!canEditUserAddress}
                    {...(!isAddressRequired && canEditUserAddress
                      ? { isClearable: true, clearLabel: t("common.action.clear") }
                      : { isClearable: false })}
                    rules={{
                      validate: {
                        required: (value) => {
                          const selectedRole = form.getValues("role");

                          if (value || selectedRole?.isAdmin) {
                            return undefined;
                          }

                          return t("components.form.error.required", {
                            inputName: t("page.create-user.form.company.label"),
                          });
                        },
                      },
                    }}
                  />
                </FormField>
              )}
              {sessionUser.project.type === "companyBased" && (
                <FormField label={t("page.create-user.form.occupation.label")}>
                  <FormInput<CreateOrEditUserFormValues, "occupation">
                    name="occupation"
                    placeholder={t("page.create-user.form.occupation.placeholder")}
                    type="text"
                    rules={{
                      maxLength: {
                        message: t("components.form.error.max-length", { length: MAX_LENGTH }),
                        value: MAX_LENGTH,
                      },
                    }}
                  />
                </FormField>
              )}
              <FormField label={t("page.create-user.form.language.label")} required>
                <FormSelect<CreateOrEditUserFormValues, LanguageDto>
                  name="uiLanguage"
                  placeholder={t("page.create-user.form.language.placeholder")}
                  items={sortedUiLanguages}
                  keySelector={(x) => x.id}
                  renderOption={(x) => x.description}
                  rules={{
                    required: t("components.form.error.required", {
                      inputName: t("page.create-user.form.language.label"),
                    }),
                  }}
                />
              </FormField>
              {sessionUser.isSuperAdmin && (
                <FormField label={t("page.create-user.form.auto-translation-language.label")}>
                  <AutoTranslationLanguageSelector
                    languages={autoTranslationLanguages}
                    selectedLanguage={currAutoTranslationLanguage}
                    onSelectLanguage={(language) => {
                      form.setValue("autoTranslationLanguageId", language?.id, {
                        shouldDirty: true,
                      });
                    }}
                  />
                </FormField>
              )}

              {currRole?.isAdmin && hasPermission((x) => x.userManagement.canEditAdminInfo) && (
                <FormField label={t("page.create-user.form.chat-permission.label")} id="chat-settings">
                  <FormRadioCardGroup>
                    <FormRadioCard<CreateOrEditUserFormValues>
                      label={t("page.create-user.form.chat-permission.option.enabled")}
                      description={t("page.create-user.form.chat-permission.option.enabled.description")}
                      name="chatPermission"
                      value="enabled"
                    />
                    <FormRadioCard<CreateOrEditUserFormValues>
                      label={t("page.create-user.form.chat-permission.option.send-only")}
                      description={t("page.create-user.form.chat-permission.option.send-only.description")}
                      name="chatPermission"
                      value="sendOnly"
                    />
                  </FormRadioCardGroup>
                </FormField>
              )}
              <Stack gap="md">
                {sessionUser.isSuperAdmin && (
                  <>
                    <FormCheckbox<CreateOrEditUserFormValues>
                      name="onboardingComplete"
                      label={t("page.create-user.form.onboarding-complete.label")}
                    />
                    {currRole?.isAdmin && (
                      <FormCheckbox<CreateOrEditUserFormValues>
                        name="isOnlyAllowedOwnTickets"
                        label={t("page.create-user.form.is-only-allowed-own-tickets.label")}
                      />
                    )}
                  </>
                )}
                {currRole?.isAdmin && hasPermission((x) => x.userManagement.canEditAdminInfo) && (
                  <FormCheckbox<CreateOrEditUserFormValues>
                    name="isAssignableToTickets"
                    label={t("page.create-user.form.is-assignable")}
                  />
                )}
                {hasPermission((x) => x.userManagement.canDeleteUser) && (
                  <>
                    <FormCheckbox<CreateOrEditUserFormValues>
                      name="deactivateAfter"
                      label={t("page.create-user.form.deactivation-date.toggle.label")}
                    />
                    {deactivateAfter && (
                      <FormField label={t("page.create-user.form.deactivation-date.picker.label")} required>
                        <FormDatePicker<CreateOrEditUserFormValues, "deactivateAfterDate">
                          name="deactivateAfterDate"
                          placeholder={t("page.create-user.form.deactivation-date.picker.placeholder")}
                          min={minActivationDate}
                          rules={{
                            validate: {
                              laterThanMin: (startDate) => {
                                if (!startDate) {
                                  return undefined;
                                }

                                return startDate < minActivationDate
                                  ? t("page.create-user.form.deactivation-date.picker.error.must-be-in-future")
                                  : undefined;
                              },
                            },
                            required: {
                              message: t("components.form.error.required", {
                                inputName: t("page.create-user.form.deactivation-date.picker.label"),
                              }),
                              value: true,
                            },
                          }}
                        />
                      </FormField>
                    )}
                  </>
                )}
              </Stack>
            </Form.Body>
            <Form.Footer
              renderPrimaryAction={() => (
                <Button type="submit" isLoading={isSubmitting} data-testid="user-add-submit-btn">
                  {t("page.create-user.form.submit.new")}
                </Button>
              )}
              renderSecondaryAction={() => (
                <Button
                  data-testid="user-add-cancel-btn"
                  variant="subtle"
                  tone="error"
                  onClick={() => {
                    if (Object.keys(form.formState.dirtyFields).length > 0) {
                      cancelModalHandler.setTrue();
                    } else {
                      goBack();
                    }
                  }}
                >
                  {t("common.action.cancel")}
                </Button>
              )}
              renderTertiaryAction={() => (
                <FormCheckbox<CreateOrEditUserFormValues> name="addMore" label={t("page.create-user.form.add-more")} />
              )}
            />
          </Form.Container>
        </Form.Root>
      </DocumentPage>

      <ConfirmModal
        tone="primary"
        title={t("page.create-user.cancel-modal.title")}
        description={t("page.create-user.cancel-modal.description")}
        isLoading={false}
        onReject={cancelModalHandler.setFalse}
        onOpenChange={cancelModalHandler.set}
        rejectBtnProps={{
          "data-testid": "cancel-modal-cancel",
        }}
        onResolve={goBack}
        resolveBtnProps={{
          text: t("common.action.ok"),
          "data-testid": "cancel-modal-confirm",
        }}
        isOpened={isCancelModalOpened}
        shouldCloseOnEsc
        data-testid="cancel-modal"
      />
    </>
  );
}
